import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { AdminAuditLogRow, AuditActionType } from "@/admin/types/admin";

interface AuditLogsFilters {
  adminUserId?: string;
  actionType?: AuditActionType;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}

export function useAuditLogs(filters: AuditLogsFilters = {}) {
  const { adminUserId, actionType, dateFrom, dateTo, page = 0, pageSize = 25 } = filters;

  return useQuery({
    queryKey: ["admin-audit-logs", filters],
    queryFn: async (): Promise<{ rows: AdminAuditLogRow[]; total: number }> => {
      let q = supabase
        .from("audit_logs")
        .select(`
          id,
          timestamp,
          admin_user_id,
          action_type,
          target_type,
          target_id,
          reason_code,
          notes,
          before_state,
          after_state,
          admin_users!inner(user_id)
        `, { count: "exact" })
        .order("timestamp", { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);

      if (adminUserId) q = q.eq("admin_user_id", adminUserId);
      if (actionType) q = q.eq("action_type", actionType);
      if (dateFrom) q = q.gte("timestamp", dateFrom);
      if (dateTo) q = q.lte("timestamp", dateTo);

      const { data, error, count } = await q;
      if (error) throw error;

      const rows: AdminAuditLogRow[] = (data ?? []).map((row: any) => ({
        id: row.id,
        timestamp: row.timestamp,
        adminUserId: row.admin_user_id,
        actionType: row.action_type as AuditActionType,
        targetType: row.target_type,
        targetId: row.target_id,
        reasonCode: row.reason_code ?? null,
        notes: row.notes ?? null,
        beforeState: row.before_state ?? null,
        afterState: row.after_state ?? null,
        adminFullName: row.admin_user_id ?? "Admin inconnu",
      }));

      return { rows, total: count ?? 0 };
    },
  });
}
